/* ============================================================
   VEDIC ANIMATIONS
   ============================================================
   Lightweight scroll-triggered reveals + number count-up.
   Runs on its own — no dependencies on the recommender JS.
   Respects prefers-reduced-motion.
   ============================================================ */

(function () {
  'use strict';

  // Skip all animations if user prefers reduced motion
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    setupScrollReveals();
    setupCountUps();
    setupBraceletReveal();
    autoApplyRevealClasses();
  }

  /* ============================================================
     1. SCROLL REVEALS
     Adds .is-revealed when element enters viewport.
     Add .vr-reveal or .vr-reveal-stagger to any element.
     ============================================================ */
  function setupScrollReveals() {
    var elements = document.querySelectorAll('.vr-reveal, .vr-reveal-stagger');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback: just reveal everything
      elements.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ============================================================
     2. COUNT-UP NUMBERS
     For elements with [data-target="N"] inside trust banner.
     Format: large numbers get K/+ suffix automatically.
     ============================================================ */
  function setupCountUps() {
    var counters = document.querySelectorAll('[data-target]');
    if (!counters.length) return;

    if (!('IntersectionObserver' in window)) {
      counters.forEach(function (el) { animateCounter(el); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    counters.forEach(function (el) { observer.observe(el); });
  }

  function animateCounter(el) {
    var raw = el.getAttribute('data-target');
    var target = parseInt(raw, 10);
    if (isNaN(target) || target <= 0) {
      el.textContent = raw;
      return;
    }

    var duration = 1800;
    var startTime = null;
    el.classList.add('is-counting');

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.floor(eased * target);

      el.textContent = formatCount(current, target);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = formatCount(target, target);
        el.classList.remove('is-counting');
      }
    }

    requestAnimationFrame(step);
  }

  function formatCount(current, target) {
    // For large numbers, show as "Xk+" once near the end
    if (target >= 10000) {
      if (current >= 1000) {
        var k = (current / 1000);
        // While counting, show 1 decimal; at target, round and add +
        if (current === target) {
          return Math.floor(k) + 'K+';
        }
        return k.toFixed(1) + 'K';
      }
      return String(current);
    }
    if (target >= 1000) {
      return current.toLocaleString('en-IN') + (current === target ? '+' : '');
    }
    return String(current) + (current === target && target > 100 ? '+' : '');
  }

  /* ============================================================
     3. BRACELET REVEAL ANIMATION
     When the recommender result appears, briefly add a class
     to the bracelet visual to trigger the rotation animation.
     ============================================================ */
  function setupBraceletReveal() {
    // The recommender JS toggles result-display via hidden attribute.
    // Watch for that change.
    var resultDisplay = document.getElementById('vr-result-display');
    var braceletVisual = document.getElementById('vr-bracelet-visual');
    if (!resultDisplay || !braceletVisual) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'hidden' && !resultDisplay.hidden) {
          // Result just appeared — trigger animation
          braceletVisual.classList.remove('is-revealing');
          // Force reflow
          void braceletVisual.offsetWidth;
          braceletVisual.classList.add('is-revealing');
        }
      });
    });

    observer.observe(resultDisplay, { attributes: true, attributeFilter: ['hidden'] });
  }

  /* ============================================================
     4. AUTO-APPLY REVEAL CLASSES
     Adds .vr-reveal to common section elements that should fade
     in on scroll, so we don't have to edit every Liquid file.
     ============================================================ */
  function autoApplyRevealClasses() {
    var selectors = [
      '.vr-section-header',
      '.vr-step-card',
      '.vr-stone-card',
      '.vr-testimonial',
      '.vr-cred-item',
      '.vr-faq-item',
      '.vr-final-cta-inner',
      '.vr-trust-stat',
      '.vr-shipping-item',
      '.vr-press-logo'
    ];

    selectors.forEach(function (sel) {
      var els = document.querySelectorAll(sel);
      els.forEach(function (el) {
        if (!el.classList.contains('vr-reveal') &&
            !el.classList.contains('vr-reveal-stagger') &&
            !el.classList.contains('is-revealed')) {
          el.classList.add('vr-reveal');
        }
      });
    });

    // Re-run scroll reveal setup so newly-added elements get observed
    setupScrollReveals();
  }
})();
